import 'bootstrap';
import initIcons from './fontawesome';
import {computed, createApp, provide, toValue} from 'vue';
import {createPinia} from 'pinia';
import {log as rootLogger, remote} from './log';
import nowAndLater from './nowAndLater';
import initPopup from "./popup";
import {useLoginStatus} from "./Composables/useLoginStatus";
import {watchEffect} from "vue-demi";
import {onServiceWorkerActivated} from "./window-workbox";
import './logout-link';
import {useCanUpload} from "./Composables/useCanUpload";
import {examinationTemplateStoreFactory, getAllExaminationTemplates} from "./Stores/examinationTemplates";
import {getMeta} from "./utils";
import {useSiteInspectionStore} from "./EventSourcedStore/siteInspection";

const log = rootLogger.getLogger('mobile');

log.info('now in mobile');

async function fetchJson(url)
{
    return fetch(url).then(response => response.json())
}

await onServiceWorkerActivated(async () => {
    const { validStatus } = useLoginStatus();
    await validStatus;

    const canLogout = useCanUpload();
    watchEffect(() => {
        document.querySelectorAll('a[data-logout]').forEach(link => {
            link.style.display=toValue(canLogout) ? '' : 'none';
        });
        if (canLogout) {
            // every value which is not "undefined" should enable log sending, the "correct" token will be set by
            // service worker
            log.info('setting token to null');
            remote.setToken(null);
        }
    });

    let appApprovingSelector = '#trigger-selection';
    let appApprovingSelectorRoot = document.querySelector(appApprovingSelector);
    if (appApprovingSelectorRoot) {
        const pinia = createPinia();
        import('VueComponents/SiteInspectionApproving').then(async({'default': SiteInspectionApproving}) => {
            let app = createApp(SiteInspectionApproving, {
                testers: await fetchJson(appApprovingSelectorRoot.dataset.testers),
                examinationTemplateStoreFactory: await examinationTemplateStoreFactory(JSON.parse(appApprovingSelectorRoot.dataset.examinationTemplatesUrl)),
                formActionUrl: appApprovingSelectorRoot.dataset.formActionUrl,
                getRowsForPreviewsUrl: JSON.parse(appApprovingSelectorRoot.dataset.getRowsForPreviewsUrl),
                migrateSiteInspectionUrl: JSON.parse(appApprovingSelectorRoot.dataset.migrateSiteInspectionUrl),
                approvedSiteInspectionComponent: JSON.parse(appApprovingSelectorRoot.dataset.approvedSiteInspectionComponent),
                siteInspections: JSON.parse(appApprovingSelectorRoot.dataset.siteInspections),
                siteInspectionUrl: JSON.parse(appApprovingSelectorRoot.dataset.siteInspectionUrl),
                testings: JSON.parse(appApprovingSelectorRoot.dataset.testings),
                testingTypes: JSON.parse(appApprovingSelectorRoot.dataset.testingTypes),
            });
            app.config.unwrapInjectedRef = true;
            app.use(pinia);
            app.mount(appApprovingSelector);
        });
    }
});
initIcons();

nowAndLater('button[data-js="popup-href"]', selection => {
    selection.forEach(element => {
        async function popupHref(window) {
            return await fetch(element.dataset.href)
                .then(response => {
                    window.document.querySelector('html').classList.add('preview');
                    return response.text();
                });
        }
        initPopup(element, popupHref);
    });
});

nowAndLater('form[data-submit-spinner],form[data-submit-readonly]', selection => {
    selection.forEach(async (formElement) => {
        const spinnerModule = await import('./submit');
        spinnerModule.initForm(formElement);
    });
});

nowAndLater('[data-vue-trigger="additionalInformation"]', selection => {
    selection.forEach(triggerElement => {
        import('VueComponents/AdditionalInformation').then(({default: AdditionalInformation}) => {
            let app = createApp(AdditionalInformation, {
                additionalInformationData: JSON.parse(triggerElement.dataset.additionalInformationData),
                hidden: false,
                disabled: true
            });
            app.mount(triggerElement);
        });
    });
});

nowAndLater('[data-vue-trigger="import-site-inspection"]', selection => {
    selection.forEach(triggerElement => {
        const pinia = createPinia();
        import('VueComponents/SiteInspectionFromImport').then(async ({default: SiteInspectionFromImport}) => {
            let app = createApp(SiteInspectionFromImport, {
                examinationTemplateStoreFactory: await examinationTemplateStoreFactory(JSON.parse(triggerElement.dataset.examinationTemplatesUrl)),
                formActionUrl: triggerElement.dataset.formActionUrl,
                getRowsForPreviewsUrl: JSON.parse(triggerElement.dataset.getRowsForPreviewsUrl),
                importUrl: JSON.parse(triggerElement.dataset.importUrl),
                siteInspectionComponent: JSON.parse(triggerElement.dataset.siteInspection),
                siteInspectionStates: JSON.parse(triggerElement.dataset.siteInspectionStates),
                testers: await fetchJson(triggerElement.dataset.testers),
                testings: JSON.parse(triggerElement.dataset.testings),
            });
            app.config.unwrapInjectedRef = true;
            app.use(pinia);
            app.mount(triggerElement);
        });
    });
});

nowAndLater('#validationModal', selection => {
    selection.forEach(element => {
        import('bootstrap').then(({Modal}) => {
            const modal = new Modal(element);
            modal.show();
        });
    });
});

nowAndLater('[data-vue-trigger="EventSourcedTest"]', selection => {
    selection.forEach(triggerElement => {
        if (!(triggerElement instanceof Element)) {
            throw new Error('triggerElement must be a Element, but was ' + typeof triggerElement);
        }

        const pinia = createPinia();
        import('VueComponents/EventSourcedTest').then(async ({default: EventSourcedTest}) => {
            const useExaminationTemplateStore = await examinationTemplateStoreFactory(getMeta('examinationTemplates'));
            const testers = await fetchJson(getMeta('initialTesters'));
            const suspendedApp = {
                components: {EventSourcedTest},
                template: `<Suspense><EventSourcedTest siteInspectionUrl="${getMeta('siteInspections')}" testingsUrl="${getMeta('testings')}"></EventSourcedTest><template #fallback><section class="card card-body">Loading...</section></template></Suspense>`,
                setup() {
                    const examinationTemplateStore = useExaminationTemplateStore();
                    provide('examinationTemplates', computed(() => examinationTemplateStore));
                    const siteInspectionStore = useSiteInspectionStore();
                    provide('examinationTemplateRecords', computed(
                        () => getAllExaminationTemplates(siteInspectionStore.projection, examinationTemplateStore)
                    ));
                    provide('testers', computed(
                        () => testers
                    ));
                },
            };
            let app = createApp(suspendedApp);
            app.config.unwrapInjectedRef = true;
            app.use(pinia);
            app.mount(triggerElement);
        });
    });
});

nowAndLater('[data-trigger-click-destruct]', selection => {
    selection.forEach(selected => {
        selected.addEventListener('click', () => {
            document.querySelectorAll(`[data-trigger-click-destruct="${selected.getAttribute('data-trigger-click-destruct')}"]`).forEach(element => {
                element.remove();
            });
        });
    });
});
